/** 协同委外对账单 — 业务类型 / 确认状态 / 入参 */

export const STATEMENT_MODEL_TYPE = {
  PRODUCT: 1,
  PROCESS: 2,
}

export const STATEMENT_MODEL_TYPE_OPTIONS = [
  {
    label: '产品委外',
    value: STATEMENT_MODEL_TYPE.PRODUCT,
    color: 'white',
    backGroundColor: 'rgb(64,158,255)',
  },
  {
    label: '工序委外',
    value: STATEMENT_MODEL_TYPE.PROCESS,
    color: 'white',
    backGroundColor: 'rgb(230,162,60)',
  },
]

/** statusSupplierConfirm：0待确认 1已确认 2有异议 */
export const STATEMENT_CONFIRM_STATUS = {
  PENDING: 0,
  CONFIRMED: 1,
  OBJECTED: 2,
}

export const STATEMENT_CONFIRM_STATUS_OPTIONS = [
  {
    label: '待确认',
    value: STATEMENT_CONFIRM_STATUS.PENDING,
    color: 'white',
    backGroundColor: 'rgb(230,162,60)',
  },
  {
    label: '已确认',
    value: STATEMENT_CONFIRM_STATUS.CONFIRMED,
    color: 'white',
    backGroundColor: 'rgb(103,194,58)',
  },
  {
    label: '有异议',
    value: STATEMENT_CONFIRM_STATUS.OBJECTED,
    color: 'white',
    backGroundColor: 'rgb(245,108,108)',
  },
]

export function getStatementModelTypeLabel(modelType) {
  const option = STATEMENT_MODEL_TYPE_OPTIONS.find(
    (item) => Number(item.value) === Number(modelType)
  )
  return option ? option.label : '--'
}

export function getStatementConfirmStatus(source) {
  if (!source) return undefined
  const value =
    source.statusSupplierConfirm ?? source.status_supplier_confirm
  if (value === undefined || value === null || value === '') return undefined
  return Number(value)
}

export function getStatementConfirmStatusLabel(status) {
  const option = STATEMENT_CONFIRM_STATUS_OPTIONS.find(
    (item) => item.value === Number(status)
  )
  return option ? option.label : '--'
}

export function canConfirmStatement(row) {
  return getStatementConfirmStatus(row) === STATEMENT_CONFIRM_STATUS.PENDING
}

export function canObjectStatement(row) {
  const status = getStatementConfirmStatus(row)
  return (
    status === STATEMENT_CONFIRM_STATUS.PENDING ||
    status === STATEMENT_CONFIRM_STATUS.CONFIRMED
  )
}

export function canCancelStatementConfirm(row) {
  const status = getStatementConfirmStatus(row)
  return (
    status === STATEMENT_CONFIRM_STATUS.CONFIRMED ||
    status === STATEMENT_CONFIRM_STATUS.OBJECTED
  )
}

export function buildStatementConfirmFilter(statusSupplierConfirm) {
  if (
    statusSupplierConfirm === '' ||
    statusSupplierConfirm === null ||
    statusSupplierConfirm === undefined
  ) {
    return {}
  }
  return { statusSupplierConfirm: Number(statusSupplierConfirm) }
}

export function buildStatementListPayload({
  modelType,
  searchData = {},
  pageParam = { pageNum: 1, pageSize: 20 },
  orderParam = [{ asc: false, fieldName: 'createTime' }],
} = {}) {
  const filter = {
    purchasesStatementDocNumber: searchData.purchasesStatementDocNumber || '',
    searchString: searchData.searchString || '',
    ...buildStatementConfirmFilter(searchData.statusSupplierConfirm),
  }
  return {
    modelType: Number(modelType),
    filter,
    pageParam: {
      pageNum: pageParam.pageNum || 1,
      pageSize: pageParam.pageSize || 20,
    },
    orderParam: Array.isArray(orderParam) ? orderParam : [],
  }
}

export function parseObjectionFileList(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const list = JSON.parse(value)
    return Array.isArray(list) ? list : []
  } catch (error) {
    return []
  }
}

export function serializeObjectionFileList(fileList) {
  if (!fileList || !fileList.length) return undefined
  return JSON.stringify(fileList)
}

export function buildObjectStatementPayload({
  purchasesStatementDocId,
  valueSupplierObjectionRemark,
  fileList = [],
} = {}) {
  const payload = {
    purchasesStatementDocId,
    valueSupplierObjectionRemark: (valueSupplierObjectionRemark || '').trim(),
  }
  const fileStr = serializeObjectionFileList(fileList)
  if (fileStr) {
    payload.valueSupplierObjectionFileList = fileStr
  }
  return payload
}

export function normalizeStatementRow(item = {}) {
  const row = { ...item }
  if (row.modelType === undefined || row.modelType === null) {
    row.modelType = STATEMENT_MODEL_TYPE.PRODUCT
  }
  row._modelTypeLabel = getStatementModelTypeLabel(row.modelType)
  row._confirmStatus = getStatementConfirmStatus(row)
  row._confirmStatusLabel = getStatementConfirmStatusLabel(row._confirmStatus)
  row._objectionFiles = parseObjectionFileList(
    row.valueSupplierObjectionFileList
  )
  return row
}

export function formatStatementMoney(value) {
  if (value === undefined || value === null || value === '') return '--'
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const fixed = Math.round(num * 100) / 100
  const parts = fixed.toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `¥ ${parts.join('.')}`
}

export function formatStatementDate(value) {
  if (!value) return '--'
  const text = String(value)
  return text.length >= 10 ? text.slice(0, 10) : text
}

export function formatStatementDateTime(value) {
  if (!value) return '--'
  return String(value)
}

export function formatStatementSpec(row = {}) {
  const raw = row.baseMaterialSpecDesc || row.baseSpecgroupDesc || ''
  if (!raw) return '--'
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      const text = parsed
        .map((item) => {
          if (typeof item === 'string') return item
          return item.specValue || item.specKey || ''
        })
        .filter(Boolean)
        .join(' ')
      return text || '--'
    }
  } catch (error) {
    // plain text
  }
  return raw
}

function pickFirstValue(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue
    const text = String(value).trim()
    if (!text || text === '-' || text === '--') continue
    return value
  }
  return ''
}

function displayOrDash(value) {
  return value === undefined || value === null || value === '' ? '--' : value
}

/** 明细行字段对齐：兼容业务端组装字段与协同端落库字段 */
export function normalizeStatementDetailRow(item = {}) {
  const row = { ...item }
  row._specText = formatStatementSpec(row)
  row._produceDocNumber = displayOrDash(
    pickFirstValue(row.produceDocNumber, row.baseProduceDocNumber)
  )
  // 单据编号：委外订单号 / 工序委外单号（勿与收货关联号混用）
  row._docNumber = displayOrDash(
    pickFirstValue(
      row.docNumber,
      row.purchasesOrderDocNumber,
      row.outsourcingDocNumber,
      row.processOutsourcingDocNumber,
      row.baseOutsourcingDocNumber,
      row.baseProcessOutsourcingDocNumber,
      row.other1
    )
  )
  // 收货单号：关联入库/收货编号
  row._receiptNumber = displayOrDash(
    pickFirstValue(
      row.purchasesReceiptDocNumber,
      row.associationNumber,
      row.association_number,
      row.outsourcingtowarehouseNumber,
      row.other2
    )
  )
  row._globalSearchNumber = displayOrDash(
    pickFirstValue(row.globalSearchNumber, row.global_search_number)
  )
  // 协同端明细常把料号落在 baseCustomerMaterialNumber
  row._materialNumber = displayOrDash(
    pickFirstValue(
      row.baseMaterialNumber,
      row.base_material_number,
      row.baseCustomerMaterialNumber,
      row.base_customer_material_number
    )
  )
  row._materialName = displayOrDash(
    pickFirstValue(row.baseMaterialName, row.base_material_name)
  )
  row._unitName = displayOrDash(
    pickFirstValue(row.baseMaterialUnitName, row.base_material_unit_name)
  )
  row._processNumber = displayOrDash(
    pickFirstValue(row.baseProcessNumber, row.base_process_number)
  )
  row._processName = displayOrDash(
    pickFirstValue(row.baseProcessName, row.base_process_name)
  )
  row._entranceTime = displayOrDash(
    formatStatementDateTime(
      pickFirstValue(
        row.baseEntranceHomeTime,
        row.base_entrance_home_time,
        row.baseDeliveryTime,
        row.base_delivery_time,
        row.createTime,
        row.create_time
      )
    )
  )
  return row
}

export function sumStatementDetailAmount(list = []) {
  return (list || []).reduce((sum, row) => {
    const qty = Number(row.baseAmount)
    return sum + (Number.isNaN(qty) ? 0 : qty)
  }, 0)
}

export function getStatementCheckStatusLabel(statusCheck) {
  return Number(statusCheck) === 1 ? '已审核' : '未审核'
}

export function getAttachmentCount(value) {
  const list = parseObjectionFileList(value)
  return list.length
}
